/**
 * ObsidianWeb — status bar component (Task 5).
 *
 * Bottom-of-window strip showing vault path, file count, current file, auth
 * mode, and connection health. Acceptance: "Status bar shows file count and
 * vault path."
 */

export interface StatusBarProps {
  readonly vaultPath: string;
  readonly fileCount: number;
  readonly authMode: string;
  readonly activePath: string | null;
  readonly connection: 'connected' | 'connecting' | 'error';
  readonly errorText?: string;
  /** Authenticated identity (FR-10.4). Absent/null under AUTH_MODE=open. */
  readonly user?: {
    readonly label: string;
    readonly detail?: string | undefined;
  } | null;
  /** Render a logout control (oidc mode; proxy identity is upstream). */
  readonly onLogout?: (() => void) | undefined;
}

/**
 * Format the vault path for display. The full path is preserved in the
 * `title` attribute for hover; the visible label collapses very long paths
 * by keeping the protocol/leading segment and the final two segments.
 */
export function formatVaultPath(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return '(no vault)';
  if (trimmed.length <= 48) return trimmed;
  const segs = trimmed.split('/').filter((s) => s.length > 0);
  if (segs.length <= 3) return trimmed;
  const head = trimmed.startsWith('/') ? '/' : '';
  return `${head}${segs[0]}/…/${segs[segs.length - 2]}/${segs[segs.length - 1]}`;
}

/** Human-readable "12 files" / "1 file" / "0 files". */
export function formatFileCount(n: number): string {
  return `${n} ${n === 1 ? 'file' : 'files'}`;
}

export function StatusBar(props: StatusBarProps): JSX.Element {
  const dotClass =
    props.connection === 'connected'
      ? 'ow-status-ok'
      : props.connection === 'error'
        ? 'ow-status-error'
        : '';
  return (
    <div className="ow-status-bar" role="status" aria-live="polite">
      <span className={`ow-status-item ${dotClass}`} aria-label={`status ${props.connection}`}>
        ●
      </span>
      <span
        className="ow-status-item"
        title={props.vaultPath}
        data-testid="status-vault-path"
      >
        {formatVaultPath(props.vaultPath)}
      </span>
      <span className="ow-status-item" data-testid="status-file-count">
        {formatFileCount(props.fileCount)}
      </span>
      <span className="ow-status-item" data-testid="status-auth-mode">
        auth: {props.authMode}
      </span>
      {props.user ? (
        <span
          className="ow-status-item"
          data-testid="status-identity"
          title={props.user.detail ?? props.user.label}
        >
          {props.user.label}
        </span>
      ) : null}
      {props.user && props.onLogout ? (
        <button
          type="button"
          className="ow-status-item"
          data-testid="status-logout"
          onClick={props.onLogout}
          style={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            textDecoration: 'underline',
            padding: 0,
            font: 'inherit',
          }}
        >
          Log out
        </button>
      ) : null}
      <span className="ow-status-spacer" />
      {props.activePath !== null ? (
        <span className="ow-status-item" data-testid="status-active-path">
          {props.activePath}
        </span>
      ) : null}
      {props.errorText !== undefined && props.errorText.length > 0 ? (
        <span className="ow-status-item ow-status-error" data-testid="status-error">
          {props.errorText}
        </span>
      ) : null}
    </div>
  );
}

export default StatusBar;
